'use client';

import { FC, useEffect, useRef } from 'react';
import { PrismicRichText, PrismicLink, PrismicImage } from '@prismicio/react';
import { Content } from '@prismicio/client';
import { ArrowRightIcon } from '@/assets';
import dynamic from 'next/dynamic';

const AnimatedCirclesBackground = dynamic(
  () => import('@/components/AnimatedCirclesBackground'),
  { ssr: false },
);

type Props = {
  slice: Content.IntroMediaGridSlice;
};

const LeftAligned8Pics: FC<Props> = ({ slice }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const {
    title,
    description,
    primary_cta: primaryCta,
    media_items: mediaItems,
  } = slice.primary as Content.IntroMediaGridSliceLeftAligned8PicsPrimary;

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-bottom');
          entry.target.classList.remove('opacity-0', 'translate-y-12');
        }
      });
    }, observerOptions);

    const currentElements: (HTMLDivElement | null)[] = [];

    if (textRef.current) {
      const element = textRef.current;
      currentElements.push(element);
      element.classList.add(
        'opacity-0',
        'translate-y-12',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      element.style.transitionDelay = '0ms';
      observer.observe(element);
    }

    if (gridRef.current) {
      const element = gridRef.current;
      currentElements.push(element);
      element.classList.add(
        'opacity-0',
        'translate-y-12',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      element.style.transitionDelay = '200ms';
      observer.observe(element);
    }

    return () => {
      currentElements.forEach((element) => {
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);

  return (
    <section
      className="relative overflow-hidden"
      data-slice-type={slice.slice_type}
    >
      <AnimatedCirclesBackground />

      {/* Content Section */}
      <div className="relative z-10 container mx-auto px-4 py-16 md:py-16 text-white">
        <div ref={textRef} className="mx-auto text-left">
          {/* Title & Description */}
          <div className="mb-8">
            {title && (
              <div className="mb-6">
                <PrismicRichText
                  field={title}
                  components={{
                    heading1: ({ children }) => (
                      <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium leading-tight font-inter">
                        {children}
                      </h1>
                    ),
                  }}
                />
              </div>
            )}

            {description && (
              <div>
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="opacity-90 font-normal">{children}</p>
                    ),
                  }}
                />
              </div>
            )}
          </div>

          {/* CTA Button */}
          {primaryCta?.text && (
            <PrismicLink field={primaryCta} className="secondary-button">
              <ArrowRightIcon className="arrow-icon" />
              {primaryCta.text || 'Learn More'}
            </PrismicLink>
          )}
        </div>
      </div>

      {/* Media Grid */}
      <div
        ref={gridRef}
        className="relative pb-16 px-4"
        style={{ overflow: 'hidden' }}
      >
        <div
          className="grid grid-rows-2 grid-cols-4 gap-4 rounded-[20px]"
          style={{
            width: '135%',
            marginLeft: '-17.5%',
            marginRight: '-12.5%',
          }}
        >
          {[0, 1].map((row) =>
            mediaItems.slice(row * 4, row * 4 + 4).map((item, idx) => (
              <div
                key={item.media.url || `${row}-${idx}`}
                className="overflow-hidden flex-shrink-0 rounded-[20px] bg-white/5 border border-white/10 h-full w-full"
                style={{
                  gridRow: row + 1,
                  gridColumn: idx + 1,
                  clipPath:
                    idx === 0
                      ? 'inset(0 0 0 50%)'
                      : idx === 3
                        ? 'inset(0 50% 0 0)'
                        : 'none',
                }}
              >
                <PrismicImage
                  field={item.media}
                  className="object-cover w-full h-full"
                  style={{
                    objectPosition:
                      idx === 0 ? 'right' : idx === 3 ? 'left' : 'center',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
            )),
          )}
        </div>
      </div>
    </section>
  );
};

export default LeftAligned8Pics;
